/**
 * Invariant 11 : RTL dès le premier composant, jamais rétrofité.
 *
 * Le RTL ne se rajoute pas après coup — chaque `margin-left` écrit entre-temps devient une
 * correction à faire. La direction est donc pilotée ici, depuis le socle vide, et le CSS
 * n'utilise que des propriétés logiques (`margin-inline-start`, `padding-inline-end`,
 * `border-inline-start`), jamais `left` / `right`.
 */

import fr from './i18n/textes/fr.json'
import ar from './i18n/textes/ar.json'
import en from './i18n/textes/en.json'
import erreursFr from './i18n/erreurs/fr.json'
import erreursAr from './i18n/erreurs/ar.json'
import erreursEn from './i18n/erreurs/en.json'

export type Langue = 'fr' | 'ar' | 'en'

/**
 * Les langues servies, dans l'ordre du sélecteur.
 *
 * **Le français d'abord, l'arabe ensuite, l'anglais en dernier**, et ce n'est pas alphabétique :
 * c'est l'ordre d'usage sur le terrain pilote. L'anglais sert les étudiants en échange et les
 * expatriés — une minorité réelle, mais une minorité.
 *
 * Cette liste est la **source unique** : le sélecteur, la validation du préfixe d'URL et la
 * relecture de `localStorage` la citent tous. Ajouter une quatrième langue, c'est ajouter une
 * entrée ici, un bloc dans `TEXTES` et un dans `ERREURS` — rien d'autre.
 */
export const LANGUES: readonly Langue[] = ['fr', 'ar', 'en'] as const

/**
 * Le nom de chaque langue **dans cette langue**.
 *
 * Jamais traduit : un lecteur arabophone cherche « العربية » dans la liste, pas « Arabe ». C'est
 * la seule façon qu'un sélecteur de langue serve à quelqu'un qui ne lit pas la langue courante —
 * c'est-à-dire exactement les gens qui en ont besoin.
 */
export const NOMS_DE_LANGUE: Record<Langue, string> = {
  fr: 'Français',
  ar: 'العربية',
  en: 'English',
}

function estUneLangue(valeur: string | null | undefined): valeur is Langue {
  return valeur !== null && valeur !== undefined && (LANGUES as readonly string[]).includes(valeur)
}

/**
 * Remplit un gabarit — « Étape {n} sur {total} » — avec ses valeurs.
 *
 * Les nombres passent par `Intl` (règle 9 du §6 de L10) : « 2 400 » en français, « ٢٬٤٠٠ » en
 * arabe. Un gabarit dont une clé manque garde son accolade, visible — c'est voulu : une valeur
 * absente se voit à la recette, une valeur silencieusement vide non.
 */
export function interpoler(
  gabarit: string,
  valeurs: Record<string, string | number>,
  langue?: Langue,
): string {
  return gabarit.replace(/\{(\w+)\}/g, (tout, cle: string) => {
    const valeur = valeurs[cle]
    if (valeur === undefined) return tout
    return typeof valeur === 'number' ? new Intl.NumberFormat(langue ?? 'fr').format(valeur) : valeur
  })
}

export function directionDe(langue: Langue): 'ltr' | 'rtl' {
  return langue === 'ar' ? 'rtl' : 'ltr'
}

/** Applique la langue au document. Aucun rechargement : la même application sert les trois. */
export function appliquerLangue(langue: Langue): void {
  const racine = document.documentElement
  racine.lang = langue
  racine.dir = directionDe(langue)
  appliquerLeManifeste(langue)
}

/**
 * Le manifeste PWA suit la langue.
 *
 * **Un manifeste ne porte qu'UNE langue** — `name`, `short_name`, `description`, `lang`, `dir`
 * y sont des valeurs uniques, sans variantes. Une application servie en trois langues depuis un
 * seul document HTML a donc besoin de trois manifestes, et d'en désigner un au démarrage.
 *
 * Sans cela, quelqu'un qui installe l'application depuis `/ar/` obtient une icône nommée en
 * français sur son écran d'accueil, et l'application s'ouvre sur `/` — donc sur la langue du
 * navigateur, pas celle qu'il avait choisie. Le `start_url` de chaque manifeste porte le préfixe
 * de sa langue : c'est lui qui fait qu'une application installée en arabe rouvre en arabe.
 *
 * Le manifeste du français reste celui que `vite-plugin-pwa` engendre et injecte dans
 * `index.html` : il est donc servi même si ce script ne tourne jamais. Les deux autres sont des
 * fichiers statiques de `public/`.
 */
function appliquerLeManifeste(langue: Langue): void {
  const lien = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')

  if (lien === null) {
    return
  }

  lien.href = langue === 'fr' ? '/manifest.webmanifest' : `/manifeste-${langue}.webmanifest`
}

export function langueInitiale(): Langue {
  const enregistree = localStorage.getItem('coloc.langue')

  if (estUneLangue(enregistree)) {
    return enregistree
  }

  // `navigator.language` rend une étiquette BCP 47 — « ar-MA », « en-GB ». On ne compare que la
  // sous-étiquette primaire : un anglophone britannique lit l'anglais.
  const preferee = navigator.language.split('-')[0]

  return estUneLangue(preferee) ? preferee : 'fr'
}

export function enregistrerLangue(langue: Langue): void {
  localStorage.setItem('coloc.langue', langue)
}

/**
 * La langue portée par le chemin, ou `null` si le chemin n'en porte pas.
 *
 * Elle prime sur `localStorage` : un lien reçu dans une langue s'ouvre dans cette langue, même
 * chez quelqu'un dont la préférence est l'autre. C'est tout l'objet du préfixe — sans cette
 * priorité, l'URL porterait la langue sans jamais la décider.
 */
export function langueDeLUrl(chemin: string): Langue | null {
  const premier = chemin.split('/')[1]

  return estUneLangue(premier) ? premier : null
}

/**
 * Le même chemin, sous un autre préfixe de langue. Sert à la bascule et à la redirection.
 *
 * Le préfixe retiré est celui que `langueDeLUrl` a reconnu, et non un motif écrit à la main : une
 * quatrième langue ajoutée à `LANGUES` sans être ajoutée au motif produirait `/en/fr/recherche`,
 * un chemin que plus rien ne rattraperait.
 */
export function urlDansLaLangue(langue: Langue, chemin: string): string {
  const portee = langueDeLUrl(chemin)
  const sansPrefixe = portee === null ? chemin : chemin.slice(portee.length + 1)

  return `/${langue}${sansPrefixe === '' ? '/' : sansPrefixe}`
}

/**
 * Les clés de texte : dérivées du dictionnaire français, qui fait référence. Une clé absente d'une
 * autre langue est une erreur de compilation — `TEXTES` exige `Record<Cles, string>` pour chacune.
 */
export type Cles = keyof typeof fr

/**
 * Les textes de l’interface, par langue — L11 : ils vivent dans `src/i18n/textes/*.json`, une
 * seule source pour le web et l'application mobile, qui les copie. Modifier le JSON, jamais ceci.
 */
export const TEXTES: Record<Langue, Record<Cles, string>> = { fr, ar, en }

/**
 * Rendu des codes d'erreur de l'API.
 *
 * L'API renvoie un **code** stable, jamais une phrase (invariant 11) : c'est ici, et seulement
 * ici, qu'il devient du texte. Un code inconnu tombe sur un message générique plutôt que sur une
 * chaîne vide — un écran muet est pire qu'un message imprécis.
 */
const ERREURS: Record<Langue, Record<string, string>> = { fr: erreursFr, ar: erreursAr, en: erreursEn }

/**
 * Le rendu d'un code d'erreur.
 *
 * `reessayerDans` vient de l'en-tête `Retry-After`. Quand il est là, on dit le délai : « patientez »
 * sans durée laisse l'utilisateur réessayer immédiatement, c'est-à-dire faire durer la limitation.
 *
 * **Un code inconnu ne dit plus « Réessayez » à tort** — 16/09/2026. Le catalogue s'était arrêté
 * à L4 ; chaque code de L6 → L8 tombait sur le message générique, qui invitait à refaire exactement
 * ce que le serveur venait de refuser (« Un bail actif ne s'annule pas » → « Réessayez »). Deux
 * replis avant le générique, dans cet ordre : le **statut HTTP** quand l'appelant le donne — un 404
 * dit « n'existe pas », un 409 « plus possible dans cet état » — puis la **forme du code**, parce
 * que les codes de l'API sont nommés avec constance (`*.not_found`, `*.not_owner`, `*.already_*`,
 * `*_invalid`, `*_too_long`). Le second repli n'est qu'un filet : le catalogue reste la règle.
 */
export function messageErreur(langue: Langue, code: string, reessayerDans?: number, statut?: number): string {
  if (code === 'rate_limited' && reessayerDans !== undefined && reessayerDans > 0) {
    const minutes = new Intl.NumberFormat(langue === 'ar' ? 'ar-MA' : 'fr-MA').format(
      Math.max(1, Math.ceil(reessayerDans / 60)),
    )

    return TEXTES[langue].erreurTropDeTentativesDelai.replace('{minutes}', minutes)
  }

  const catalogue = ERREURS[langue]

  return (
    catalogue[code] ??
    catalogue[cleDeStatut(statut ?? statutDeduit(code))] ??
    catalogue.internal
  )
}

/** La clé de repli d'un statut HTTP ; `''` quand il n'y en a pas, et la recherche tombe à plat. */
function cleDeStatut(statut: number | undefined): string {
  if (statut === undefined) return ''
  if (statut >= 500) return 'status.5xx'
  if (statut === 400 || statut === 422) return 'status.400'

  return `status.${statut}`
}

/** Ce que la forme d'un code laisse deviner de son statut — un filet, jamais la règle. */
function statutDeduit(code: string): number | undefined {
  if (/(\.|_)not_found$/.test(code)) return 404
  if (/\.(not_owner|not_a_party|not_party|not_member|not_author|not_issuer|not_subject|not_respondent|not_eligible|referent_required)$/.test(code)) return 403
  if (/\.(already_\w+|not_pending|not_active|not_draft|not_cancellable|closed|expired|settled|deleted|no_model|quota_exceeded)$/.test(code)) return 409
  if (/(_invalid|_required|_too_long|_negative|_mismatch|_out_of_range|_unknown|_unsupported|_empty|_missing)$/.test(code)) return 400

  return undefined
}

/**
 * La phrase d'une cause quelconque — `ErreurApi`, panne réseau, ou n'importe quoi d'autre.
 *
 * Structurel plutôt qu'`instanceof` : ce module ne dépend pas d'`api.ts`, et une erreur venue d'un
 * autre bundle (le service worker, un iframe) garderait sa forme sans garder sa classe. Un
 * `fetch` qui n'aboutit pas lève un `TypeError` sans statut : c'est le réseau, pas le serveur.
 */
export function phraseDErreur(langue: Langue, cause: unknown): string {
  if (estErreurApi(cause)) {
    return messageErreur(langue, cause.code, cause.reessayerDans, cause.statut)
  }

  if (cause instanceof TypeError) {
    return ERREURS[langue].reseau
  }

  return ERREURS[langue].internal
}

export function estErreurApi(
  cause: unknown,
): cause is { code: string; statut: number; reessayerDans?: number } {
  return (
    typeof cause === 'object' &&
    cause !== null &&
    typeof (cause as { code?: unknown }).code === 'string' &&
    typeof (cause as { statut?: unknown }).statut === 'number'
  )
}
